import { Alert, Button, Card, Form, Input, Radio, Space, Typography, message as antdMessage } from "antd";
import { useEffect, useState, type ComponentProps } from "react";
import { useNavigate } from "react-router";
import { InputNumber as ZhInputNumber } from "zhihao-ui-react";

import request from "@/api";

// beta.13 的类型声明未暴露 controls，保留旧 Vue 数字输入的无步进配置。
type ProjectInputNumberProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const integerInputProps: ProjectInputNumberProps = {
    controls: false,
    min: 0,
    precision: 0,
    placeholder: "请输入ID"
};
const quantityInputProps: ProjectInputNumberProps = {
    controls: false,
    min: 1,
    precision: 0,
    placeholder: "请输入采购吨数"
};
const moneyInputProps: ProjectInputNumberProps = {
    controls: false,
    min: 0,
    precision: 2,
    placeholder: "请输入金额"
};

export interface ProjectFormPageProps {
    projectId?: number | string;
}

/** 复用 Vue 项目详情接口，供编辑表单回显。 */
async function loadProjectForm(id: number | string) {
    const { data } = await request.service.get(`/admin/project/vo/${id}`);
    return data as any;
}

/** 采购项目表单最小闭环，保持 Vue 新增/编辑 payload 结构。 */
export function ProjectFormPage({ projectId }: ProjectFormPageProps) {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [error, setError] = useState("");
    useEffect(() => {
        if (!projectId) return;
        void loadProjectForm(projectId)
            .then(data => {
                const project = data.project || {};
                const extend = data.projectExtends?.[0] || {};
                form.setFieldsValue({ ...project, purType: project.purType, ...extend });
            })
            .catch(() => setError("项目详情加载失败"));
    }, [form, projectId]);
    const submit = async (values: any) => {
        setError("");
        const extend = {
            projectId: projectId ? Number(projectId) : undefined,
            buyerId: Number(values.buyerId),
            purQuantity: Number(values.purQuantity),
            buyerUnitPrice: Number(values.buyerUnitPrice),
            buyerFrozenAmount: Number(values.buyerFrozenAmount),
            buyerContractAmount: Number(values.buyerContractAmount),
            buyerMinPayLimit: Number(values.buyerMinPayLimit)
        };
        const payload = {
            name: values.name,
            productId: Number(values.productId),
            purType: Number(values.purType),
            remark: values.remark,
            type: Number(values.type || 1),
            projectExtendForms: [extend],
            serviceCosts: []
        };
        try {
            if (projectId) await request.service.put(`/admin/project/${projectId}`, payload);
            else await request.service.post("/admin/project", payload);
            const message = projectId ? "项目编辑成功" : "项目新增成功";
            antdMessage.success(message);
        } catch {
            // 保存失败时保留表单并给出稳定的页面内反馈，避免依赖 toast 生命周期。
            setError(projectId ? "项目编辑失败" : "项目新增失败");
            antdMessage.error(projectId ? "项目编辑失败" : "项目新增失败");
        }
    };
    return (
        <section aria-label="project-form-page">
            <Card>
                <Typography.Title level={4}>项目表单</Typography.Title>
                {error ? <Alert message={error} role="alert" type="error" /> : null}
                <Form form={form} layout="vertical" onFinish={submit} initialValues={{ purType: 1 }}>
                    <Form.Item label="采购类型" name="purType">
                        <Radio.Group>
                            <Radio value={1}>联合采购</Radio>
                            <Radio value={2}>直采</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label="项目名称" name="name" rules={[{ required: true, message: "请输入项目名称！" }]}>
                        <Input placeholder="请输入项目名称" />
                    </Form.Item>
                    <Form.Item label="项目介绍" name="remark" rules={[{ required: true, message: "请输入项目介绍！" }]}>
                        <Input.TextArea placeholder="请输入项目介绍" />
                    </Form.Item>
                    <Form.Item label="商品ID" name="productId" rules={[{ required: true, message: "请输入商品ID！" }]}>
                        <ZhInputNumber {...integerInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="采购商账号ID"
                        name="buyerId"
                        rules={[{ required: true, message: "请输入采购商账号ID！" }]}
                    >
                        <ZhInputNumber {...integerInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="意向采购吨数"
                        name="purQuantity"
                        rules={[{ required: true, message: "请输入意向采购吨数！" }]}
                    >
                        <ZhInputNumber {...quantityInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="预估单价"
                        name="buyerUnitPrice"
                        rules={[{ required: true, message: "请输入预估单价！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="冻结金额"
                        name="buyerFrozenAmount"
                        rules={[{ required: true, message: "请输入冻结金额！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="合同金额"
                        name="buyerContractAmount"
                        rules={[{ required: true, message: "请输入合同金额！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="最低预付款提醒额度"
                        name="buyerMinPayLimit"
                        rules={[{ required: true, message: "请输入最低预付款提醒额度！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Space className="operation-fixed-btn">
                        <Button autoInsertSpace={false} htmlType="submit" type="primary">
                            提交
                        </Button>
                        <Button onClick={() => void navigate("/purchasingManage/project")}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}

export default ProjectFormPage;
